<script setup lang="ts">
/**
 * Detail body — renders identically inside the drawer chrome OR on a
 * page wrapped in a `<Box padding="4">`. Props in, nothing else.
 */
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import StatusBadge from '@uiKit/components/StatusBadge.vue'
import CopyButton from '@uiKit/components/CopyButton.vue'
import { formatDateTime, formatKey, formatMoney } from '@shared/utils'
import type { DepositRequestDetail } from '../types/depositRequestsTypes'

const props = defineProps<{
  details: DepositRequestDetail
  // Investor-entered bank UTR. Sourced from the list row by the
  // drawer-container — the detail endpoint's `proof` omits it and only
  // carries the auto-generated paymentReference.
  investorUtr?: string | null
}>()

const investorFullName = computed(() => {
  const first = props.details.investor.firstName ?? ''
  const last = props.details.investor.lastName ?? ''
  return `${first} ${last}`.trim() || '—'
})

const amountLabel = computed(() => formatMoney(props.details.currencyCode, props.details.amount))

const showAuditSection = computed(
  () =>
    !!props.details.audit.approvedBy ||
    !!props.details.audit.approvedAt ||
    !!props.details.audit.approvedBankReference ||
    !!props.details.completedAt ||
    !!props.details.rejectionReason,
)
</script>

<template>
  <Box flex direction="col" gap="4">
    <Box flex direction="col" gap="3">
      <Box flex direction="col" gap="1">
        <Text type="caption" color="muted">Amount</Text>
        <Text type="display" weight="bold" style="font-variant-numeric: tabular-nums">
          {{ amountLabel }}
        </Text>
      </Box>

      <Box flex justify="between">
        <Text type="body-sm" color="muted">Submitted</Text>
        <Text type="body-sm" weight="medium">{{ formatDateTime(details.createdAt) }}</Text>
      </Box>

      <Box v-if="investorUtr" flex justify="between" align="center" gap="2">
        <Text type="body-sm" color="muted">UTR</Text>
        <Box flex align="center" gap="2">
          <Text type="body-sm" weight="medium">{{ investorUtr }}</Text>
          <CopyButton :value="investorUtr" label="Copy" copied-label="Copied" />
        </Box>
      </Box>

      <Box v-if="details.proof.paymentReference" flex justify="between" align="center" gap="2">
        <Text type="body-sm" color="muted">Payment reference</Text>
        <Box flex align="center" gap="2">
          <Text type="body-sm" weight="medium">{{ details.proof.paymentReference }}</Text>
          <CopyButton :value="details.proof.paymentReference" label="Copy" copied-label="Copied" />
        </Box>
      </Box>

      <Box v-if="details.proof.documentUrl" flex justify="between" align="center" gap="2">
        <Text type="body-sm" color="muted">Document</Text>
        <a
          :href="details.proof.documentUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="document-link"
        >
          <Text type="body-sm" weight="medium" color="primary">Open in new tab</Text>
        </a>
      </Box>
    </Box>

    <Box class="section">
      <Text
        type="caption-sm"
        transform="uppercase"
        weight="semibold"
        color="muted"
        class="section-label"
        >Investor</Text
      >
      <Box flex direction="col" gap="2" class="rows">
        <Box flex justify="between">
          <Text type="body-sm" color="muted">Name</Text>
          <Text type="body-sm" weight="medium">{{ investorFullName }}</Text>
        </Box>
        <Box v-if="details.investor.email" flex justify="between">
          <Text type="body-sm" color="muted">Email</Text>
          <Text type="body-sm" weight="medium">{{ details.investor.email }}</Text>
        </Box>
        <Box v-if="details.investor.kycStatus" flex justify="between">
          <Text type="body-sm" color="muted">KYC</Text>
          <StatusBadge
            :color="details.investor.kycStatus === 'approved' ? 'success' : 'warning'"
            :label="formatKey(details.investor.kycStatus)"
          />
        </Box>
      </Box>
    </Box>

    <Box v-if="details.treasuryBankAccount" class="section">
      <Text
        type="caption-sm"
        transform="uppercase"
        weight="semibold"
        color="muted"
        class="section-label"
        >Treasury account</Text
      >
      <Box flex direction="col" gap="2" class="rows">
        <Box flex justify="between">
          <Text type="body-sm" color="muted">Account name</Text>
          <Text type="body-sm" weight="medium">{{ details.treasuryBankAccount.accountName }}</Text>
        </Box>
        <Box flex justify="between">
          <Text type="body-sm" color="muted">Bank</Text>
          <Text type="body-sm" weight="medium">{{ details.treasuryBankAccount.bankName }}</Text>
        </Box>
        <Box flex justify="between">
          <Text type="body-sm" color="muted">Currency</Text>
          <Text type="body-sm" weight="medium">{{ details.treasuryBankAccount.currencyCode }}</Text>
        </Box>
        <Box v-if="details.treasuryBankAccount.iban" flex justify="between">
          <Text type="body-sm" color="muted">IBAN</Text>
          <Text type="body-sm" weight="medium">{{ details.treasuryBankAccount.iban }}</Text>
        </Box>
      </Box>
    </Box>

    <Box v-if="showAuditSection" class="section">
      <Text
        type="caption-sm"
        transform="uppercase"
        weight="semibold"
        color="muted"
        class="section-label"
        >Audit</Text
      >
      <Box flex direction="col" gap="2" class="rows">
        <Box v-if="details.audit.approvedBy" flex justify="between">
          <Text type="body-sm" color="muted">Approved by</Text>
          <Text type="body-sm" weight="medium">{{ details.audit.approvedBy }}</Text>
        </Box>
        <Box v-if="details.audit.approvedAt" flex justify="between">
          <Text type="body-sm" color="muted">Approved at</Text>
          <Text type="body-sm" weight="medium">{{ formatDateTime(details.audit.approvedAt) }}</Text>
        </Box>
        <Box v-if="details.audit.approvedBankReference" flex justify="between">
          <Text type="body-sm" color="muted">Bank reference</Text>
          <Text type="body-sm" weight="medium">{{ details.audit.approvedBankReference }}</Text>
        </Box>
        <Box v-if="details.completedAt" flex justify="between">
          <Text type="body-sm" color="muted">Completed at</Text>
          <Text type="body-sm" weight="medium">{{ formatDateTime(details.completedAt) }}</Text>
        </Box>
        <Box v-if="details.rejectionReason" flex justify="between" gap="3">
          <Text type="body-sm" color="muted">Rejection reason</Text>
          <Text type="body-sm" weight="medium" align="right">{{ details.rejectionReason }}</Text>
        </Box>
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.section {
  border-top: 1px solid var(--p-content-border-color);
  padding-top: 1rem;
}
.section-label {
  display: block;
  margin-bottom: 0.5rem;
}
.rows {
  padding-top: 0.25rem;
}
.document-link {
  text-decoration: none;
}
.document-link:hover {
  text-decoration: underline;
}
</style>
